import React, { useState } from 'react';
import { X, Plus, Frown, Annoyed, Meh, Smile, SmilePlus } from 'lucide-react';
import { ABLE_COLORS } from '../../constants';

// Mood icon scale (Lucide icons for consistency)
const MOOD_ICONS = [
  { level: 1, icon: Frown, label: 'Rough' },
  { level: 2, icon: Annoyed, label: 'Meh' },
  { level: 3, icon: Meh, label: 'Okay' },
  { level: 4, icon: Smile, label: 'Good' },
  { level: 5, icon: SmilePlus, label: 'Great' }
];

// Descriptor categories - no colors, emoji carries the color
const DESCRIPTOR_CATEGORIES = [
  {
    name: 'Positive Energy',
    descriptors: ['Happy', 'Excited', 'Motivated', 'Energized', 'Confident', 'Hopeful', 'Grateful', 'Proud', 'Inspired', 'Playful']
  },
  {
    name: 'Calm/Content',
    descriptors: ['Calm', 'Content', 'Relaxed', 'Peaceful', 'Cozy', 'Comfortable']
  },
  {
    name: 'Productive States',
    descriptors: ['Focused', 'Productive', 'Creative', 'In flow', 'Determined']
  },
  {
    name: 'Neutral/Mixed',
    descriptors: ['Okay', 'Fine', 'Meh', 'Distracted', 'Scattered', 'Restless']
  },
  {
    name: 'Low Energy',
    descriptors: ['Tired', 'Exhausted', 'Drained', 'Foggy', 'Flat', 'Sluggish']
  },
  {
    name: 'Difficult Feelings',
    descriptors: ['Anxious', 'Stressed', 'Frustrated', 'Overwhelmed', 'Irritated', 'Sad', 'Down', 'Lonely', 'Discouraged', 'Numb']
  }
];

/**
 * MoodModal - Rich mood selection with emoji + descriptors
 */
export default function MoodModal({ currentMood, onSave, onClose }) {
  const [level, setLevel] = useState(currentMood?.level ?? null);
  const [descriptors, setDescriptors] = useState(currentMood?.descriptors || []);
  const [custom, setCustom] = useState(currentMood?.custom || '');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const toggleDescriptor = (desc) => {
    if (descriptors.includes(desc)) {
      setDescriptors(descriptors.filter(d => d !== desc));
    } else {
      setDescriptors([...descriptors, desc]);
    }
  };

  const handleAddCustom = () => {
    if (custom.trim() && !descriptors.includes(custom.trim())) {
      setDescriptors([...descriptors, custom.trim()]);
      setCustom('');
      setShowCustomInput(false);
    }
  };

  const handleSave = () => {
    // If no level selected, clear mood entirely
    if (level === null) {
      onSave(null);
      return;
    }
    
    onSave({
      level,
      descriptors,
      custom: custom.trim() || undefined
    });
  };

  // Allow saving with null level (to clear mood)
  const canSave = true;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        maxWidth: '400px',
        width: '100%',
        maxHeight: '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        left: 'calc(var(--chat-width, 0px) / -2)',
        transition: 'left 0.2s ease'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: ABLE_COLORS.textPrimary
          }}>
            How are you feeling?
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} color={ABLE_COLORS.textMuted} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Mood Icon Scale */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
              {MOOD_ICONS.map((mood) => {
                const isSelected = level === mood.level;
                const color = ABLE_COLORS.mood[mood.level];
                const Icon = mood.icon;
                return (
                  <button
                    key={mood.level}
                    onClick={() => setLevel(level === mood.level ? null : mood.level)}
                    style={{
                      flex: 1,
                      padding: '12px 8px',
                      background: isSelected ? color : 'transparent',
                      border: 'none',
                      borderRadius: ABLE_COLORS.radiusMedium,
                      cursor: 'pointer',
                      transition: 'all 150ms ease',
                      boxShadow: isSelected ? `0 2px 8px ${color}40` : 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Icon 
                      size={32} 
                      color={isSelected ? 'white' : ABLE_COLORS.textMuted}
                      strokeWidth={2}
                    />
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: isSelected ? 'white' : ABLE_COLORS.textMuted
                    }}>
                      {mood.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Descriptors */}
          <div>
            <p style={{
              fontSize: '13px',
              fontWeight: '600',
              color: ABLE_COLORS.textMuted,
              marginBottom: '12px'
            }}>
              What words fit? (optional)
            </p>

            {DESCRIPTOR_CATEGORIES.map((category) => (
              <div key={category.name} style={{ marginBottom: '16px' }}>
                <p style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: ABLE_COLORS.textMuted,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {category.name}
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '6px'
                }}>
                  {category.descriptors.map((desc) => {
                    const isSelected = descriptors.includes(desc);
                    const selectedColor = level ? ABLE_COLORS.mood[level] : ABLE_COLORS.primary;
                    return (
                      <button
                        key={desc}
                        onClick={() => toggleDescriptor(desc)}
                        style={{
                          padding: '8px 10px',
                          background: isSelected ? selectedColor : ABLE_COLORS.bgSubtle,
                          border: isSelected ? 'none' : `1px solid ${ABLE_COLORS.border}`,
                          borderRadius: ABLE_COLORS.radiusSmall,
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: isSelected ? '600' : '500',
                          color: isSelected ? 'white' : ABLE_COLORS.textSecondary,
                          transition: 'all 150ms ease',
                          boxShadow: isSelected ? `0 2px 8px ${selectedColor}40` : 'none',
                          textAlign: 'center'
                        }}
                      >
                        {desc}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Custom Input */}
            <div style={{ marginTop: '16px' }}>
              {showCustomInput ? (
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <input
                    type="text"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCustom()}
                    placeholder="Type your own..."
                    autoFocus
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: `1px solid ${ABLE_COLORS.border}`,
                      borderRadius: ABLE_COLORS.radiusSmall,
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleAddCustom}
                    disabled={!custom.trim()}
                    style={{
                      padding: '8px 16px',
                      background: custom.trim() ? ABLE_COLORS.primary : ABLE_COLORS.bgSubtle,
                      border: 'none',
                      borderRadius: ABLE_COLORS.radiusSmall,
                      cursor: custom.trim() ? 'pointer' : 'default',
                      color: custom.trim() ? 'white' : ABLE_COLORS.textMuted,
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    Add
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowCustomInput(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: ABLE_COLORS.textMuted,
                    fontSize: '13px'
                  }}
                >
                  <Plus size={16} />
                  Add your own
                </button>
              )}
            </div>

            {/* Selected descriptors display */}
            {descriptors.length > 0 && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: ABLE_COLORS.bgSubtle,
                borderRadius: ABLE_COLORS.radiusSmall
              }}>
                <p style={{
                  fontSize: '11px',
                  color: ABLE_COLORS.textMuted,
                  marginBottom: '8px'
                }}>
                  Selected:
                </p>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px'
                }}>
                  {descriptors.map((desc) => {
                    const selectedColor = level ? ABLE_COLORS.mood[level] : ABLE_COLORS.primary;
                    return (
                      <span
                        key={desc}
                        onClick={() => toggleDescriptor(desc)}
                        style={{
                          padding: '4px 10px',
                          background: selectedColor,
                          borderRadius: ABLE_COLORS.radiusSmall,
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        {desc} ×
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${ABLE_COLORS.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: `1px solid ${ABLE_COLORS.border}`,
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: ABLE_COLORS.textSecondary
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{
              padding: '10px 20px',
              background: canSave ? ABLE_COLORS.primary : ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: canSave ? 'pointer' : 'default',
              fontSize: '14px',
              fontWeight: '600',
              color: canSave ? 'white' : ABLE_COLORS.textMuted
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
